import { computed, markRaw } from 'vue'
import { useUserStore } from '@/stores/userStore'
import { t } from '@/locales'
import type { IMenuRailItem } from '@/components/appLayout/AppLayoutMenuRail.vue'
import IconLucideHouse from '~icons/lucide/house'
import IconLucidePackage from '~icons/lucide/package'
import IconLucideUsers from '~icons/lucide/users'
import IconLucideCircleDollarSign from '~icons/lucide/circle-dollar-sign'
import IconLucideSettings from '~icons/lucide/settings'

export type { IMenuRailItem }

export function useNavItems() {
  const userStore = useUserStore()

  return computed<IMenuRailItem[]>(() => {
    const settingsTargetRoute = userStore.canUser('settingsGeneral.view') ||
      userStore.canUser('settingsBranding.view') ||
      userStore.canUser('employees.view') ||
      userStore.canUser('equipment.view') ||
      userStore.canUser('setup.view')
      ? 'Settings'
      : userStore.canUser('reportTemplates.view')
        ? 'SettingsReports'
        : userStore.canUser('translations.view')
          ? 'LabTranslations'
          : null

    const items = [
      { path: 'Dashboard', title: t('main-menu.item.home'), icon: markRaw(IconLucideHouse) },
      {
        path: 'Orders',
        title: t('main-menu.item.orders'),
        icon: markRaw(IconLucidePackage),
        activeRoutes: ['OrdersDeliveries', 'OrdersAnalyses'],
      },
      userStore.canUser('customers.view')
        ? { path: 'Customers', title: t('main-menu.item.customers'), icon: markRaw(IconLucideUsers) }
        : null,
      userStore.canUser('orders.billing')
        ? { path: 'Invoices', title: t('main-menu.item.invoices'), icon: markRaw(IconLucideCircleDollarSign) }
        : null,
      // Settings, LabTranslations, and SettingsReports are three separate
      // top-level route trees that share one nav entry and sidebar. Route to
      // whichever tree this role can actually reach — a role with only
      // translations.view (e.g. Lab Assistant) has nothing under the
      // Settings tree itself, so linking there would bounce them straight
      // back out via the permissions guard.
      settingsTargetRoute
        ? {
            id: 'settings',
            path: settingsTargetRoute,
            title: t('main-menu.item.settings'),
            icon: markRaw(IconLucideSettings),
            activeRoutes: [
              'SettingsReports',
              'Employees',
              'LabEquipment',
              'LabEquipmentMaintenance',
              'LabTranslations',
              'PricingSegments',
              'PricingSegmentEdit',
            ],
          }
        : null,
    ].filter((item): item is NonNullable<typeof item> => item !== null)

    return items
  })
}
